import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { getPost } from "@/content/blog";
import { BLOG_IMAGE_FALLBACK } from "@/content/blog/generated-image-manifest";
import { MarkdownBody } from "@/components/MarkdownBody";
import { MediaImage } from "@/components/MediaImage";
import { PageHero } from "@/components/PageHero";
import { CtaBand } from "@/components/sections/CtaBand";
import { RelatedNews } from "@/components/sections/RelatedNews";
import { BlogTldr, BlogTldrBoundary } from "@/components/BlogTldr";
import { extractTldrSection, getTldrSummary } from "@/lib/tldr";
import {
  breadcrumbSchema,
  blogMetaTitle,
  cleanBlogTitle,
  metaDescription,
  ORGANIZATION_ID,
  PERSON_ID,
  SITE_URL,
} from "@/lib/seo";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = getPost(params.slug);
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData }) => {
    const imagePath = loaderData?.image ?? BLOG_IMAGE_FALLBACK;
    const image = toAbsoluteUrl(imagePath);
    const imageSize = imagePath.includes("/images/brand/")
      ? { width: "1200", height: "750" }
      : { width: "1600", height: "1000" };
    const title = loaderData ? cleanBlogTitle(loaderData.title) : "";
    const description = loaderData ? metaDescription(loaderData.description) : "";
    const canonical = loaderData ? `${SITE_URL}/blog/${loaderData.slug}` : "";

    return {
      meta: loaderData
        ? [
            { title: blogMetaTitle(loaderData.title, loaderData.slug) },
            { name: "description", content: description },
            { property: "og:title", content: title },
            { property: "og:description", content: description },
            { property: "og:url", content: canonical },
            { property: "og:type", content: "article" },
            { name: "twitter:card", content: "summary_large_image" },
            { name: "twitter:title", content: title },
            { name: "twitter:description", content: description },
            { property: "og:image", content: image },
            { property: "og:image:width", content: imageSize.width },
            { property: "og:image:height", content: imageSize.height },
            { name: "twitter:image", content: image },
          ]
        : [],
      links: loaderData ? [{ rel: "canonical", href: canonical }] : [],
      scripts: loaderData
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                headline: title,
                description,
                datePublished: getPublishedIsoDate(loaderData),
                dateModified: loaderData.lastUpdated || undefined,
                author: { "@id": PERSON_ID },
                publisher: { "@id": ORGANIZATION_ID },
                mainEntityOfPage: canonical,
                image,
                inLanguage: "en-GB",
              }),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                breadcrumbSchema([
                  { name: "Home", url: `${SITE_URL}/` },
                  { name: "Blog", url: `${SITE_URL}/blog` },
                  { name: title, url: canonical },
                ]),
              ),
            },
          ]
        : [],
    };
  },
  notFoundComponent: () => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-4xl">Article not found</h1>
      <Link
        to="/blog"
        className="mt-6 inline-flex items-center gap-2 text-sm text-foreground underline-offset-4 hover:underline"
      >
        Back to all articles <ArrowUpRight className="h-4 w-4" />
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-3xl">Something went wrong</h1>
      <p className="mt-3 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: BlogPostPage,
});

function toAbsoluteUrl(path: string): string {
  return path.startsWith("http") ? path : `https://www.phil-carr.co.uk${path}`;
}

function BlogPostPage() {
  const post = Route.useLoaderData();
  const displayTitle = cleanBlogTitle(post.title);
  const bodyWithoutTitle = post.body.replace(/^#\s+.+\n+/, "");
  const publishedDate = getPublishedDate(post);
  const lastUpdatedDate = post.lastUpdated ? formatDate(post.lastUpdated) : "";
  const bodyWithoutDate = bodyWithoutTitle.replace(/\*\*Published:\*\*[^\n]*\n?/gi, "");
  const { tldr: extractedTldr, body: bodyWithoutTldr } = extractTldrSection(bodyWithoutDate);
  const { image: heroImage, body: bodyWithoutImage } = extractFirstImage(bodyWithoutTldr);
  // Prefer an authored TL;DR section; otherwise always synthesise the summary
  // from the body so it reflects the full article, not just the opening copy.
  const tldr = extractedTldr || getTldrSummary(bodyWithoutImage) || post.description;
  const bodyWithoutAuthor = stripLegacyAuthorSection(bodyWithoutImage);
  return (
    <>
      <PageHero title={displayTitle} compact />
      <BlogTldrBoundary>
        <article className="inner-content-section container mx-auto max-w-4xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
          {/*
          Article header strip: back link + date meta share one container with
          consistent vertical rhythm so the spacing above the TL;DR card stays
          identical whether or not a date is present, and the two pieces can't
          collide at narrow widths regardless of content length.
        */}
          <div className="mb-8 flex flex-col gap-3 sm:mb-10">
            <div>
              <Link to="/blog" className="demo-back-link demo-link-arrow-button">
                <ArrowLeft className="h-4 w-4" />
                All articles
              </Link>
            </div>
            {(publishedDate || lastUpdatedDate) && (
              <p className="m-0 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                {publishedDate && (
                  <span>
                    <span className="font-semibold">Published:</span>{" "}
                    <time dateTime={post.date || undefined}>{publishedDate}</time>
                  </span>
                )}
                {lastUpdatedDate && (
                  <span>
                    <span className="font-semibold">Last updated:</span>{" "}
                    <time dateTime={post.lastUpdated || undefined}>{lastUpdatedDate}</time>
                  </span>
                )}
              </p>
            )}
          </div>
          <BlogTldr tldr={tldr} />
          <div className="mt-10">
            {heroImage && (
              <figure className="demo-image-frame mx-auto mb-8 w-full max-w-full md:float-right md:mx-6 md:mb-6 md:mt-2 md:max-w-xs">
                <MediaImage
                  src={heroImage.src}
                  alt={heroImage.alt}
                  fallbackSrc={BLOG_IMAGE_FALLBACK}
                />
              </figure>
            )}
            <MarkdownBody imageFallback={BLOG_IMAGE_FALLBACK} flatHeadings>
              {bodyWithoutAuthor}
            </MarkdownBody>
            <AuthorEeatBlock />
          </div>
        </article>
      </BlogTldrBoundary>
      <RelatedNews count={3} exclude={[post.slug]} title="More articles" eyebrow="From the blog" />
      <CtaBand
        title="Need help applying this to your site?"
        body="If something in this article rings true for your business, let's have a quick conversation about it."
      />
    </>
  );
}

function AuthorEeatBlock() {
  return (
    <aside
      className="blog-author-card not-prose"
      aria-labelledby="blog-author-title"
      itemScope
      itemType="https://schema.org/Person"
      itemID={PERSON_ID}
    >
      <div className="blog-author-mark" aria-hidden="true">
        PC
      </div>
      <div className="blog-author-copy">
        <p className="demo-section-label blog-author-kicker">About the author</p>
        <h2 id="blog-author-title" itemProp="name">
          Phil Carr
        </h2>
        <p itemProp="description">
          Phil Carr is a Barnsley-based SEO consultant and web strategist with 12+ years
          of practical experience helping businesses across Yorkshire and the UK improve
          search visibility, website performance, and enquiry quality.
        </p>
        <ul className="blog-author-proof" aria-label="Phil Carr background">
          <li>Technical SEO, local SEO, content strategy, and conversion-led web design.</li>
          <li>Google Analytics and Google Ads certified, with ongoing industry testing.</li>
          <li>Focused on measurable business outcomes, not rankings in isolation.</li>
        </ul>
        <Link to="/about" className="demo-link-arrow-button blog-author-link">
          Read Phil's background
          <span className="demo-arrow-circle demo-arrow-small" aria-hidden="true">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </Link>
      </div>
    </aside>
  );
}

function stripLegacyAuthorSection(body: string): string {
  return body
    .replace(/\n##\s+About the author\s*\n[\s\S]*?(?=\n##\s+|$)/i, (section) =>
      /Phil Carr/i.test(section) ? "\n" : section,
    )
    .trim();
}

function getPublishedDate(post: ReturnType<typeof getPost>): string {
  if (post?.date) {
    return formatDate(post.date);
  }
  return post?.body.match(/\*\*Published:\*\*\s*([^\n]+)/i)?.[1]?.trim() ?? "";
}

function getPublishedIsoDate(post: ReturnType<typeof getPost>): string | undefined {
  if (post?.date) return post.date;

  const published = post?.body.match(/\*\*Published:\*\*\s*([^\n]+)/i)?.[1]?.trim();
  if (!published) return undefined;

  const parsed = new Date(`${published} UTC`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString().slice(0, 10);
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function extractFirstImage(body: string): {
  image: { alt: string; src: string } | null;
  body: string;
} {
  const match = body.match(/^\s*!\[([^\]]*)\]\(([^)]+)\)\s*$/m);
  if (!match) return { image: null, body };
  const [full, alt, src] = match;
  return { image: { alt, src }, body: body.replace(full, "").trim() };
}
